// Dnexus CRM — Business OS landing page.
// Brand: matches the Dnexus logo — gold (#FFD24D→#EFA800) + charcoal ink
// (#0F172A). Gold CTAs always carry dark ink text for contrast. White base,
// amber tints, glass accents, rounded cards, scroll reveals, repeated trial
// CTAs. Product mockups are CSS-built; photography via Unsplash + portrait
// avatars for real-world feel.
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  Users, BarChart3, Clock, Shield, Building2, Zap, Target, ArrowRight, Check,
  TrendingUp, Star, Phone, Mail, Globe, Lock, Cloud,
  Menu, X, MessageCircle, Sparkles, ChevronDown, MapPin, CalendarDays,
  ClipboardList, Wallet, Smartphone, FileBarChart, LayoutDashboard, UserCog
} from "lucide-react";

const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_539426ce-fb0d-461c-961c-2751960afc8c/artifacts/r2mo7zys_Dnexus%20Logo%20Light.png";
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_539426ce-fb0d-461c-961c-2751960afc8c/artifacts/fhqddo5o_Dnexus%20nexus%20logo%20Dark.png";

const font = { fontFamily: "Inter, sans-serif" };
const fontHead = { fontFamily: "Poppins, sans-serif" };

// ---- Brand tokens (from the Dnexus logo) ----
const GOLD = "#EFA800";           // logo gold
const GOLD_DEEP = "#A16207";      // gold for text/links on light
const INK = "#0F172A";            // logo charcoal ink
const GOLD_GRAD = "linear-gradient(135deg,#FFD24D,#EFA800)";
const GOLD_SHADOW = "0 12px 28px -8px rgba(202,138,4,.45)";

// ---- Photography ----
const PHOTO_TEAM = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1000&q=80&auto=format&fit=crop";
const PHOTO_OFFICE = "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1000&q=80&auto=format&fit=crop";
const PHOTO_CTA = "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1600&q=80&auto=format&fit=crop";
const AVATARS = [
  "https://randomuser.me/api/portraits/men/32.jpg",
  "https://randomuser.me/api/portraits/women/65.jpg",
  "https://randomuser.me/api/portraits/men/85.jpg",
  "https://randomuser.me/api/portraits/women/44.jpg",
];

// ---- Scroll-reveal helpers ----
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } }
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

function Section({ children, className = "", id, style }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.section ref={ref} id={id} initial="hidden" animate={isInView ? "visible" : "hidden"} variants={stagger} className={className} style={style}>
      {children}
    </motion.section>
  );
}

// Animated counter — counts up when scrolled into view.
function CountUp({ end, suffix = "", decimals = 0, duration = 1600 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!isInView) return;
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(end * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isInView, end, duration]);
  const shown = decimals > 0 ? val.toFixed(decimals) : Math.round(val).toLocaleString("en-IN");
  return <span ref={ref}>{shown}{suffix}</span>;
}

// Gold CTA — logo-gold gradient with dark ink text (AA contrast).
function GoldBtn({ to, href, children, className = "", testId, big = false }) {
  const cls = `inline-flex items-center justify-center gap-2 font-extrabold rounded-full transition-all hover:-translate-y-0.5 hover:brightness-105 ${big ? "text-[15.5px] px-8 py-4" : "text-[14px] px-6 py-3"} ${className}`;
  const sty = { ...font, background: GOLD_GRAD, color: INK, boxShadow: GOLD_SHADOW };
  if (to) return <Link to={to} data-testid={testId} className={cls} style={sty}>{children}</Link>;
  return <a href={href} data-testid={testId} className={cls} style={sty}>{children}</a>;
}

// ==================== NAVBAR ====================
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const links = [
    { label: "Features", href: "#features" },
    { label: "Pricing", href: "#pricing" },
    { label: "Screenshots", href: "#screenshots" },
    { label: "FAQ", href: "#faq" },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? "bg-white/85 backdrop-blur-2xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] border-b border-gray-100" : "bg-white/60 backdrop-blur-xl"
    }`} data-testid="landing-navbar">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-[76px] lg:h-[88px]">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <img src={LOGO_DARK} alt="Dnexus CRM" className="h-12 lg:h-[60px] w-auto" />
          </Link>
          <div className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <a key={l.href} href={l.href} className="text-[13.5px] font-semibold text-gray-500 hover:text-gray-900 transition-colors" style={font}>{l.label}</a>
            ))}
          </div>
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-btn" className="text-[13.5px] font-semibold text-gray-600 hover:text-gray-900 px-4 py-2 transition-colors" style={font}>
              Log in
            </Link>
            <GoldBtn to="/login" testId="nav-trial-btn" className="text-[13.5px] px-5 py-2.5">Start Free 10-Day Trial</GoldBtn>
          </div>
          <button className="lg:hidden p-2 text-gray-600" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-5 py-4 space-y-1 shadow-xl">
          {links.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setMobileOpen(false)} className="block py-2.5 text-[15px] font-medium text-gray-600" style={font}>{l.label}</a>
          ))}
          <div className="flex gap-3 pt-3">
            <Link to="/login" className="flex-1 text-center py-2.5 text-sm font-semibold text-gray-600 border border-gray-200 rounded-full" style={font}>Log in</Link>
            <Link to="/login" className="flex-1 text-center py-2.5 text-sm font-extrabold rounded-full" style={{ ...font, background: GOLD_GRAD, color: INK }}>Start Free Trial</Link>
          </div>
        </div>
      )}
    </nav>
  );
}
// ==================== HERO ====================
// The differentiator is one login → many businesses, so the hero *demonstrates*
// it: a live switcher cycles real businesses and the KPIs tween to each one.
const HERO_BUSINESSES = [
  { name: "Digital Nexus Media", industry: "Marketing Agency", accent: "#EFA800",
    kpis: { Leads: 1248, Staff: 24, Present: 21, Tasks: 96 }, revenue: 1840000 },
  { name: "Rajshree Hospital", industry: "Healthcare", accent: "#0EA5E9",
    kpis: { Leads: 3120, Staff: 148, Present: 132, Tasks: 210 }, revenue: 6210000 },
  { name: "SRMS Trust", industry: "Education", accent: "#8B5CF6",
    kpis: { Leads: 4452, Staff: 96, Present: 88, Tasks: 174 }, revenue: 4170000 },
  { name: "Growteq Agri Farms", industry: "Agriculture", accent: "#10B981",
    kpis: { Leads: 148, Staff: 12, Present: 11, Tasks: 38 }, revenue: 790000 },
];

// Tweens between values whenever `value` changes (unlike CountUp, which fires once).
function Tween({ value, format }) {
  const [shown, setShown] = useState(value);
  const fromRef = useRef(value);
  useEffect(() => {
    const from = fromRef.current, to = value, start = performance.now(), dur = 650;
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setShown(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
      else fromRef.current = to;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{format ? format(shown) : shown.toLocaleString("en-IN")}</>;
}

const lakhs = (n) => `₹${(n / 100000).toFixed(1)}L`;

function LiveSwitcherCard() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const biz = HERO_BUSINESSES[idx];

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_BUSINESSES.length), 3200);
    return () => clearInterval(t);
  }, [paused]);

  return (
    <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="absolute -inset-10 pointer-events-none rounded-[48px]"
        style={{ background: `radial-gradient(55% 55% at 55% 35%, ${biz.accent}22, transparent 70%)`, transition: "background .6s" }} />
      <div className="relative rounded-[22px] border border-gray-200/80 bg-white shadow-[0_30px_80px_-28px_rgba(15,23,42,0.35)] overflow-hidden">
        {/* window chrome */}
        <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-gray-100 bg-gray-50/80">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          <span className="ml-3 text-[10.5px] font-semibold text-gray-400" style={font}>crm.dnexusmedia.com</span>
        </div>

        {/* the switcher — the actual product's signature control */}
        <div className="px-4 pt-4">
          <div className="text-[10px] font-bold uppercase tracking-[.12em] text-gray-400 mb-2" style={font}>Switch business</div>
          <div className="flex flex-wrap gap-1.5">
            {HERO_BUSINESSES.map((b, i) => (
              <button key={b.name} type="button" onClick={() => setIdx(i)}
                className="text-[11px] font-bold rounded-full px-3 py-1.5 border transition-all"
                style={i === idx
                  ? { background: b.accent, borderColor: b.accent, color: "#fff", boxShadow: `0 6px 16px -6px ${b.accent}` }
                  : { background: "#fff", borderColor: "#E5E7EB", color: "#64748B" }}
                aria-pressed={i === idx}>
                {b.name}
              </button>
            ))}
          </div>
        </div>

        {/* active business header */}
        <div className="px-4 pt-4 flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-extrabold text-[15px] shrink-0"
            style={{ background: biz.accent, transition: "background .5s" }}>{biz.name.charAt(0)}</div>
          <div className="min-w-0">
            <div className="text-[14px] font-extrabold truncate" style={{ ...fontHead, color: INK }}>{biz.name}</div>
            <div className="text-[11px] text-gray-400" style={font}>{biz.industry}</div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-[9.5px] font-bold uppercase tracking-wide text-gray-400" style={font}>Revenue</div>
            <div className="text-[15px] font-extrabold tabular-nums" style={{ ...fontHead, color: biz.accent, transition: "color .5s" }}>
              <Tween value={biz.revenue} format={lakhs} />
            </div>
          </div>
        </div>

        {/* KPIs that retune per business */}
        <div className="grid grid-cols-2 min-[380px]:grid-cols-4 gap-2 px-4 py-4">
          {Object.entries(biz.kpis).map(([label, val]) => (
            <div key={label} className="rounded-xl border border-gray-100 bg-gray-50/60 p-2.5">
              <div className="text-[8.5px] font-bold uppercase tracking-wide text-gray-400" style={font}>{label}</div>
              <div className="text-[17px] font-extrabold tabular-nums leading-tight" style={{ ...fontHead, color: INK }}>
                <Tween value={val} />
              </div>
            </div>
          ))}
        </div>

        {/* footer proof line */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-t border-gray-100 bg-gray-50/60">
          <span className="relative flex w-2 h-2">
            <span className="absolute inline-flex w-full h-full rounded-full opacity-60 animate-ping" style={{ background: biz.accent }} />
            <span className="relative inline-flex w-2 h-2 rounded-full" style={{ background: biz.accent }} />
          </span>
          <span className="text-[11px] text-gray-500" style={font}>
            Same login. Same dashboard. <b style={{ color: INK }}>Zero data mixing.</b>
          </span>
        </div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <div className="relative overflow-hidden pt-28 lg:pt-36 pb-16 lg:pb-24" style={{ background: "linear-gradient(180deg,#FFFBEB 0%,#FFFFFF 85%)" }}>
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(234,179,8,.14), transparent 65%)" }} />
      <div className="absolute top-40 -left-40 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(15,23,42,.06), transparent 65%)" }} />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-14 items-center">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 mb-6" style={{ borderColor: "#FDE68A", background: "#FFFBEB" }}>
            <Sparkles className="w-3.5 h-3.5" style={{ color: GOLD_DEEP }} />
            <span className="text-[11.5px] font-bold tracking-wide" style={{ ...font, color: GOLD_DEEP }}>The Business Operating System</span>
          </motion.div>

          <motion.h1 variants={fadeUp} className="text-[38px] leading-[1.05] sm:text-[52px] lg:text-[60px] font-extrabold tracking-[-0.02em]" style={{ ...fontHead, color: INK }}>
            Run every business<br className="hidden sm:block" /> you own from{" "}
            <span className="relative inline-block">
              <span style={{ background: "linear-gradient(90deg,#D99700,#A16207)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>one login</span>
              <svg className="absolute -bottom-1 left-0 w-full" height="9" viewBox="0 0 200 9" fill="none" preserveAspectRatio="none" aria-hidden>
                <path d="M2 6.5C48 2.5 152 1.5 198 5.5" stroke="#EFA800" strokeWidth="3.5" strokeLinecap="round" opacity=".45" />
              </svg>
            </span>
          </motion.h1>

          <motion.p variants={fadeUp} className="mt-6 text-[16.5px] lg:text-[17.5px] leading-relaxed text-gray-500 max-w-[34rem]" style={font}>
            Leads, employees, attendance, payroll, tasks and AI conversations — for
            <b style={{ color: INK }}> all of your companies</b>, in one dashboard. Switch between them
            instantly; their data never mixes.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-8 flex flex-col sm:flex-row gap-3">
            <GoldBtn to="/login" testId="hero-trial-btn" big>Start Free 10-Day Trial <ArrowRight className="w-4 h-4" /></GoldBtn>
            <button type="button" onClick={openScheduleDemo} data-testid="hero-demo-btn"
              className="inline-flex items-center justify-center gap-2 text-[15px] font-bold bg-white border border-gray-200 rounded-full px-7 py-3.5 shadow-sm transition-all hover:border-amber-300 hover:-translate-y-0.5"
              style={{ ...font, color: INK }}>
              <CalendarDays className="w-4 h-4" /> Book a Live Demo
            </button>
          </motion.div>

          {/* one consolidated trust line instead of three stacked strips */}
          <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3">
            <div className="flex items-center gap-2.5">
              <div className="flex -space-x-2.5">
                {AVATARS.slice(0, 4).map((a, i) => (
                  <img key={i} src={a} alt="" loading="lazy" className="w-8 h-8 rounded-full border-2 border-white object-cover shadow-sm" />
                ))}
              </div>
              <div className="text-[12.5px] text-gray-500 leading-tight" style={font}>
                <span className="font-bold" style={{ color: INK }}>500+ businesses</span>
                <span className="ml-1.5 inline-flex items-center gap-0.5 align-middle">
                  {Array.from({ length: 5 }).map((_, s) => <Star key={s} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
                </span>
              </div>
            </div>
            <span className="hidden sm:block w-px h-5 bg-gray-200" />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12.5px] text-gray-400" style={font}>
              {["No credit card", "Full access", "Free onboarding"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" />{t}</span>
              ))}
            </div>
          </motion.div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="pb-4">
          <LiveSwitcherCard />
        </motion.div>
      </div>
    </div>
  );
}

// ==================== TRUST / STATS ====================
function TrustStats() {
  const stats = [
    { end: 500, suffix: "+", label: "Businesses" },
    { end: 10000, suffix: "+", label: "Employees Managed" },
    { end: 1, suffix: " Million+", label: "Attendance Records" },
    { end: 99.9, suffix: "%", decimals: 1, label: "Uptime" },
  ];
  return (
    <Section className="py-14 border-y border-amber-100/70" style={{}}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-8 items-center">
          {stats.map((s) => (
            <motion.div key={s.label} variants={fadeUp} className="text-center">
              <div className="text-3xl lg:text-4xl font-extrabold" style={{ ...fontHead, color: GOLD_DEEP }}>
                <CountUp end={s.end} suffix={s.suffix} decimals={s.decimals || 0} />
              </div>
              <div className="mt-1 text-[13px] font-medium text-gray-500" style={font}>{s.label}</div>
            </motion.div>
          ))}
          <motion.div variants={fadeUp} className="col-span-2 lg:col-span-1 flex justify-center">
            <div className="inline-flex items-center gap-2.5 rounded-2xl border px-5 py-3" style={{ borderColor: "#FDE68A", background: "#FFFBEB" }}>
              <Shield className="w-6 h-6" style={{ color: GOLD_DEEP }} />
              <div className="text-left">
                <div className="text-[13px] font-bold text-gray-800" style={font}>Secure Cloud</div>
                <div className="text-[11px] text-gray-500" style={font}>Infrastructure</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}

// ==================== FEATURES ====================
const FEATURES = [
  { icon: Target, title: "Lead Management", grad: GOLD_GRAD, dark: true, shadow: "rgba(202,138,4,.3)",
    items: ["Lead Capture", "Sales Pipeline", "Follow-up Reminders", "Lead Assignment", "Status Tracking"] },
  { icon: UserCog, title: "HR Management", grad: "linear-gradient(135deg,#7C3AED,#C084FC)", shadow: "rgba(124,58,237,.25)",
    items: ["Employee Database", "Departments", "Payroll", "Leave Management", "Documents"] },
  { icon: Clock, title: "Attendance Management", grad: "linear-gradient(135deg,#0D9488,#2DD4BF)", shadow: "rgba(13,148,136,.25)",
    items: ["GPS Attendance", "Selfie Attendance", "QR Attendance", "Shift Management", "Overtime", "Live Location"] },
  { icon: ClipboardList, title: "Task Management", grad: "linear-gradient(135deg,#0F172A,#334155)", shadow: "rgba(15,23,42,.3)",
    items: ["Assign Tasks", "Team Collaboration", "Deadlines", "Progress Tracking", "Notifications"] },
  { icon: Building2, title: "Multi Business Support", grad: "linear-gradient(135deg,#DB2777,#F472B6)", shadow: "rgba(219,39,119,.25)",
    items: ["Unlimited Businesses", "Separate Employees", "Separate Leads", "Separate Reports", "Individual Settings"] },
  { icon: BarChart3, title: "Reports & Analytics", grad: "linear-gradient(135deg,#D97706,#FBBF24)", shadow: "rgba(217,119,6,.3)",
    items: ["HR Reports", "Attendance Reports", "Lead Reports", "Employee Performance", "Business Dashboard"] },
];

function Features() {
  return (
    <Section id="features" className="py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <motion.div variants={fadeUp} className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-[12px] font-bold tracking-[.14em] uppercase" style={{ ...font, color: GOLD_DEEP }}>Everything included</span>
          <h2 className="mt-3 text-3xl lg:text-[40px] font-extrabold tracking-tight" style={{ ...fontHead, color: INK }}>One platform. Every module your business needs.</h2>
          <p className="mt-4 text-[15px] text-gray-500" style={font}>Six powerful modules working together — no more juggling separate tools.</p>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <motion.div key={f.title} variants={fadeUp}
              className="group bg-white rounded-[20px] border border-gray-100 p-6 shadow-[0_6px_24px_-8px_rgba(15,23,42,0.08)] hover:shadow-[0_18px_44px_-12px_rgba(15,23,42,0.16)] hover:-translate-y-1 hover:border-amber-200 transition-all duration-300">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform"
                style={{ background: f.grad, boxShadow: `0 10px 22px -6px ${f.shadow}` }}>
                <f.icon className="w-6 h-6" style={{ color: f.dark ? INK : "#fff" }} />
              </div>
              <h3 className="text-[17px] font-bold mb-3" style={{ ...fontHead, color: INK }}>{f.title}</h3>
              <ul className="space-y-2">
                {f.items.map((it) => (
                  <li key={it} className="flex items-center gap-2 text-[13.5px] text-gray-500" style={font}>
                    <Check className="w-3.5 h-3.5 shrink-0" style={{ color: GOLD }} /> {it}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ==================== WHY CHOOSE ====================
const WHY = [
  ["Cloud Based", Cloud], ["Mobile Friendly", Smartphone], ["Multi Business Ready", Building2], ["Fast Performance", Zap],
  ["Secure Data", Lock], ["Role Based Permissions", Shield], ["Easy to Use", Sparkles], ["Real-time Reports", TrendingUp],
  ["WhatsApp Integration", MessageCircle], ["Email Integration", Mail],
];

function WhyChoose() {
  return (
    <Section className="py-20" style={{}}>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <motion.div variants={fadeUp}>
            <span className="text-[12px] font-bold tracking-[.14em] uppercase" style={{ ...font, color: GOLD_DEEP }}>Why Dnexus CRM</span>
            <h2 className="mt-3 text-3xl lg:text-[38px] font-extrabold tracking-tight" style={{ ...fontHead, color: INK }}>Built for growing Indian businesses</h2>
            <p className="mt-4 text-[15px] leading-relaxed text-gray-500" style={font}>
              From a single shop to a multi-business group — Dnexus CRM scales with you. Your team gets a
              simple app they'll actually use; you get the visibility and control you've always wanted.
            </p>
          </motion.div>
          {/* Team photo with floating stat chips */}
          <motion.div variants={fadeUp} className="relative mt-8 mb-2">
            <img src={PHOTO_TEAM} alt="Team working together with Dnexus CRM" loading="lazy"
              className="w-full h-56 lg:h-64 object-cover rounded-[22px] shadow-[0_20px_50px_-16px_rgba(15,23,42,0.3)]" />
            <div className="absolute -bottom-4 left-5 bg-white rounded-2xl shadow-xl border border-gray-100 px-4 py-2.5 flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: GOLD_GRAD }}><TrendingUp className="w-4 h-4" style={{ color: INK }} /></span>
              <div>
                <div className="text-[12px] font-extrabold" style={{ ...fontHead, color: INK }}>+38% productivity</div>
                <div className="text-[10px] text-gray-400" style={font}>average in 3 months</div>
              </div>
            </div>
            <div className="absolute -top-4 right-5 bg-white rounded-2xl shadow-xl border border-gray-100 px-4 py-2.5 flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-full flex items-center justify-center bg-emerald-100"><Check className="w-4 h-4 text-emerald-600" /></span>
              <div className="text-[12px] font-extrabold" style={{ ...fontHead, color: INK }}>91% attendance</div>
            </div>
          </motion.div>
          <motion.div variants={fadeUp} className="mt-8">
            <GoldBtn to="/login">Start Free 10-Day Trial <ArrowRight className="w-4 h-4" /></GoldBtn>
          </motion.div>
        </div>
        <div className="grid grid-cols-2 gap-3.5">
          {WHY.map(([label, Icon]) => (
            <motion.div key={label} variants={fadeUp}
              className="flex items-center gap-3 bg-white/90 backdrop-blur rounded-2xl border border-gray-100 px-4 py-3.5 shadow-[0_4px_16px_-6px_rgba(15,23,42,0.08)] hover:border-amber-300 transition-colors">
              <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}>
                <Icon className="w-[18px] h-[18px]" style={{ color: GOLD_DEEP }} />
              </span>
              <span className="text-[13.5px] font-semibold text-gray-700" style={font}>{label}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ==================== PRICING ====================
const PLANS = [
  {
    name: "Starter", price: "₹999", per: "/month", popular: false,
    items: ["5 Users", "1 Business", "Lead Management", "HR", "Attendance", "Task Management"],
    cta: "Start Free Trial", to: "/login",
  },
  {
    name: "Professional", price: "₹2,999", per: "/month", popular: true,
    items: ["25 Users", "3 Businesses", "Advanced Reports", "Email Integration", "WhatsApp Integration", "Priority Support"],
    cta: "Start Free Trial", to: "/login",
  },
  {
    name: "Enterprise", price: "₹7,999", per: "/month", popular: false,
    items: ["Unlimited Users", "Unlimited Businesses", "White Label", "API Access", "Dedicated Support", "Custom Development"],
    cta: "Contact Sales", href: "mailto:info@dnexusmedia.com?subject=Enterprise Inquiry - Dnexus CRM",
  },
];

function Pricing() {
  return (
    <Section id="pricing" className="py-20 lg:py-24" style={{ background: "linear-gradient(180deg,#FFFFFF, #FFFBEB 50%, #FFFFFF)" }}>
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <motion.div variants={fadeUp} className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-[12px] font-bold tracking-[.14em] uppercase" style={{ ...font, color: GOLD_DEEP }}>Simple pricing</span>
          <h2 className="mt-3 text-3xl lg:text-[40px] font-extrabold tracking-tight" style={{ ...fontHead, color: INK }}>Plans that grow with your business</h2>
          <p className="mt-4 text-[15px] text-gray-500" style={font}>Every plan starts with a free 10-day trial. No credit card required.</p>
        </motion.div>
        <div className="grid lg:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((p) => (
            <motion.div key={p.name} variants={fadeUp}
              className={`relative flex flex-col rounded-[20px] p-7 transition-all duration-300 hover:-translate-y-1 ${
                p.popular
                  ? "shadow-[0_28px_64px_-16px_rgba(15,23,42,0.55)]"
                  : "bg-white border border-gray-100 shadow-[0_8px_30px_-10px_rgba(15,23,42,0.1)] hover:shadow-[0_18px_44px_-12px_rgba(15,23,42,0.16)]"
              }`}
              style={p.popular ? { background: "linear-gradient(160deg,#0F172A,#1F2937)" } : {}}>
              {p.popular && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 text-[11px] font-extrabold uppercase tracking-wide rounded-full px-4 py-1.5 shadow"
                  style={{ ...font, background: GOLD_GRAD, color: INK }}>
                  Most Popular
                </span>
              )}
              <div className="text-[15px] font-bold mb-2" style={{ ...fontHead, color: p.popular ? "#FFD24D" : INK }}>{p.name}</div>
              <div className="flex items-end gap-1 mb-6">
                <span className="text-4xl font-extrabold" style={{ ...fontHead, color: p.popular ? "#fff" : INK }}>{p.price}</span>
                <span className={`text-[13px] mb-1.5 ${p.popular ? "text-gray-400" : "text-gray-400"}`} style={font}>{p.per}</span>
              </div>
              <ul className="space-y-2.5 flex-1 mb-7">
                {p.items.map((it) => (
                  <li key={it} className={`flex items-center gap-2.5 text-[14px] ${p.popular ? "text-gray-200" : "text-gray-600"}`} style={font}>
                    <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: p.popular ? "rgba(255,210,77,.18)" : "#FFFBEB" }}>
                      <Check className="w-3 h-3" style={{ color: p.popular ? "#FFD24D" : GOLD_DEEP }} />
                    </span>
                    {it}
                  </li>
                ))}
              </ul>
              {p.to ? (
                <Link to={p.to} className="text-center text-[14.5px] font-extrabold rounded-full py-3.5 transition-all hover:-translate-y-px"
                  style={{ ...font, background: GOLD_GRAD, color: INK, boxShadow: GOLD_SHADOW }}>
                  {p.cta}
                </Link>
              ) : (
                <a href={p.href} className="text-center text-[14.5px] font-bold rounded-full py-3.5 border-2 border-gray-200 hover:border-amber-400 transition-all" style={{ ...font, color: INK }}>
                  {p.cta}
                </a>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ==================== FREE TRIAL BANNER ====================
function TrialBanner() {
  const perks = ["Full Feature Access", "No Credit Card Required", "Easy Setup", "Free Onboarding", "Free Technical Support"];
  return (
    <Section className="py-16">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <motion.div variants={fadeUp} className="relative overflow-hidden rounded-[28px] px-8 py-12 lg:px-14 lg:py-14 text-center"
          style={{ background: "linear-gradient(135deg,#FFD24D 0%,#F5B916 55%,#EFA800 100%)" }}>
          <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-white/20 pointer-events-none" />
          <div className="absolute -bottom-24 -left-16 w-80 h-80 rounded-full pointer-events-none" style={{ background: "rgba(15,23,42,.06)" }} />
          <div className="relative">
            <h2 className="text-3xl lg:text-[38px] font-extrabold tracking-tight" style={{ ...fontHead, color: INK }}>Try Dnexus CRM Free for 10 Days</h2>
            <div className="mt-7 flex flex-wrap justify-center gap-x-7 gap-y-3">
              {perks.map((t) => (
                <span key={t} className="inline-flex items-center gap-2 text-[14px] font-bold" style={{ ...font, color: "#3F2D04" }}>
                  <span className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "rgba(15,23,42,.12)" }}><Check className="w-3 h-3" style={{ color: INK }} /></span>{t}
                </span>
              ))}
            </div>
            <Link to="/login" data-testid="trial-banner-btn"
              className="mt-9 inline-flex items-center gap-2 text-[15.5px] font-extrabold rounded-full px-9 py-4 shadow-2xl hover:scale-[1.03] transition-transform"
              style={{ ...font, background: INK, color: "#FFD24D" }}>
              Start Your Free Trial <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

// ==================== SCREENSHOTS ====================
const SHOT_TABS = ["Dashboard", "Leads", "Attendance", "HR", "Tasks", "Reports"];

function MiniShot({ tab }) {
  const header = (title, color) => (
    <div className="flex items-center justify-between mb-3">
      <div className="text-[11px] font-extrabold text-gray-800" style={fontHead}>{title}</div>
      <div className="h-5 w-16 rounded-full" style={{ background: color, opacity: 0.18 }} />
    </div>
  );
  const rows = (n, c) => Array.from({ length: n }).map((_, i) => (
    <div key={i} className="flex items-center gap-2 rounded-lg border border-gray-100 px-2.5 py-2 mb-1.5 bg-white">
      <span className="w-5 h-5 rounded-full shrink-0" style={{ background: c, opacity: 0.85 - i * 0.12 }} />
      <div className="flex-1"><div className="h-1.5 rounded bg-gray-200 mb-1" style={{ width: `${80 - i * 9}%` }} /><div className="h-1.5 rounded bg-gray-100" style={{ width: `${45 - i * 4}%` }} /></div>
      <div className="h-3.5 w-10 rounded-full" style={{ background: c, opacity: 0.18 }} />
    </div>
  ));
  switch (tab) {
    case "Leads":
      return <div>{header("Lead Pipeline", GOLD)}<div className="grid grid-cols-3 gap-2">{[["New", GOLD_DEEP], ["Follow-up", "#D97706"], ["Won", "#10B981"]].map(([s, c]) => (
        <div key={s} className="rounded-xl bg-gray-50 p-2"><div className="text-[8px] font-bold mb-1.5" style={{ color: c, ...font }}>{s}</div>{Array.from({ length: 3 }).map((_, i) => (<div key={i} className="rounded-lg bg-white border border-gray-100 p-1.5 mb-1.5"><div className="h-1.5 w-4/5 rounded bg-gray-200 mb-1" /><div className="h-1.5 w-1/2 rounded bg-gray-100" /></div>))}</div>))}</div></div>;
    case "Attendance":
      return <div>{header("Attendance Dashboard", "#0D9488")}<div className="grid grid-cols-4 gap-2 mb-3">{[["Present", "78", "#10B981"], ["Absent", "5", "#EF4444"], ["Leave", "3", "#D97706"], ["WFH", "6", "#7C3AED"]].map(([l, v, c]) => (<div key={l} className="rounded-xl border border-gray-100 p-2 text-center"><div className="text-[12px] font-extrabold" style={{ color: c, ...fontHead }}>{v}</div><div className="text-[7.5px] text-gray-400 font-semibold" style={font}>{l}</div></div>))}</div>{rows(3, "#0D9488")}</div>;
    case "HR":
      return <div>{header("Employee Directory", "#7C3AED")}{rows(4, "#7C3AED")}</div>;
    case "Tasks":
      return <div>{header("Task Board", GOLD)}<div className="grid grid-cols-3 gap-2">{[["To Do", "#64748B"], ["In Progress", GOLD_DEEP], ["Done", "#10B981"]].map(([s, c]) => (<div key={s} className="rounded-xl bg-gray-50 p-2"><div className="text-[8px] font-bold mb-1.5" style={{ color: c, ...font }}>{s}</div>{Array.from({ length: 2 }).map((_, i) => (<div key={i} className="rounded-lg bg-white border border-gray-100 p-1.5 mb-1.5"><div className="h-1.5 w-4/5 rounded bg-gray-200 mb-1" /><div className="h-1 w-8 rounded-full" style={{ background: c, opacity: 0.25 }} /></div>))}</div>))}</div></div>;
    case "Reports":
      return <div>{header("Business Reports", GOLD)}<div className="flex items-end gap-1.5 h-24 mb-2">{[40, 62, 34, 76, 52, 84, 66, 92, 58, 72].map((h, i) => (<div key={i} className="flex-1 rounded-t-md" style={{ height: `${h}%`, background: i % 3 === 2 ? "#14B8A6" : i % 3 === 1 ? INK : GOLD, opacity: 0.92 }} />))}</div><div className="h-1.5 rounded bg-gray-100 w-2/3" /></div>;
    default:
      return <div>{header("Business Dashboard", GOLD)}<div className="grid grid-cols-4 gap-2 mb-3">{[["Leads", "1,248", GOLD_DEEP], ["Staff", "86", "#0D9488"], ["Present", "91%", "#10B981"], ["Tasks", "134", "#334155"]].map(([l, v, c]) => (<div key={l} className="rounded-xl border border-gray-100 p-2"><div className="text-[7.5px] text-gray-400 font-semibold uppercase" style={font}>{l}</div><div className="text-[12px] font-extrabold" style={{ color: c, ...fontHead }}>{v}</div></div>))}</div><div className="flex items-end gap-1.5 h-16">{[34, 48, 28, 58, 44, 64, 52, 72, 60, 80].map((h, i) => (<div key={i} className="flex-1 rounded-t-md" style={{ height: `${h}%`, background: i % 2 ? INK : GOLD, opacity: 0.88 }} />))}</div></div>;
  }
}

function Screenshots() {
  const [tab, setTab] = useState("Dashboard");
  return (
    <Section id="screenshots" className="py-20" style={{ background: "linear-gradient(180deg,#FFFFFF,#FFFBEB 55%,#FFFFFF)" }}>
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <motion.div variants={fadeUp} className="text-center max-w-2xl mx-auto mb-10">
          <span className="text-[12px] font-bold tracking-[.14em] uppercase" style={{ ...font, color: GOLD_DEEP }}>Product tour</span>
          <h2 className="mt-3 text-3xl lg:text-[40px] font-extrabold tracking-tight" style={{ ...fontHead, color: INK }}>See Dnexus CRM in action</h2>
          <p className="mt-4 text-[15px] text-gray-500" style={font}>Clean, fast screens your team will love — on laptop and mobile.</p>
        </motion.div>
        <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-2 mb-9">
          {SHOT_TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`text-[13px] font-bold rounded-full px-5 py-2 transition-all ${tab === t ? "" : "bg-white text-gray-500 border border-gray-200 hover:border-amber-300"}`}
              style={tab === t ? { ...font, background: GOLD_GRAD, color: INK, boxShadow: GOLD_SHADOW } : font}>
              {t}
            </button>
          ))}
        </motion.div>
        <motion.div variants={fadeUp} className="flex flex-col lg:flex-row items-center justify-center gap-10">
          {/* Laptop mockup */}
          <div className="w-full max-w-2xl">
            <div className="rounded-t-2xl border border-gray-200 border-b-0 px-4 pt-3 pb-0" style={{ background: INK }}>
              <div className="flex items-center gap-1.5 pb-2.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400" /><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /><span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
              </div>
              <div className="bg-white rounded-t-xl p-4 min-h-[260px]">
                <MiniShot tab={tab} />
              </div>
            </div>
            <div className="h-4 rounded-b-2xl bg-gray-200 shadow-inner mx-[-14px]" />
          </div>
          {/* Phone mockup */}
          <div className="w-[190px] shrink-0 rounded-[30px] p-2 shadow-[0_28px_60px_-16px_rgba(15,23,42,0.4)]" style={{ background: INK }}>
            <div className="bg-white rounded-[24px] overflow-hidden">
              <div className="px-4 py-3" style={{ background: GOLD_GRAD }}>
                <div className="text-[10px] font-extrabold" style={{ ...font, color: INK }}>Dnexus Mobile App</div>
                <div className="text-[8px]" style={{ ...font, color: "#5B4304" }}>Hi, Rahul 👋</div>
              </div>
              <div className="p-3 space-y-2">
                <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2.5 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-emerald-600" />
                  <div><div className="text-[9px] font-bold text-gray-800" style={font}>Punch In</div><div className="text-[7.5px] text-gray-400" style={font}>GPS + Selfie</div></div>
                </div>
                {[["My Tasks", "6 pending", ClipboardList, GOLD_DEEP], ["My Leads", "12 assigned", Target, "#7C3AED"], ["Payslip", "June ready", Wallet, "#D97706"], ["Leave", "Apply now", CalendarDays, "#0D9488"]].map(([t, s, Icon, c]) => (
                  <div key={t} className="rounded-xl border border-gray-100 px-3 py-2 flex items-center gap-2">
                    <Icon className="w-4 h-4" style={{ color: c }} />
                    <div><div className="text-[9px] font-bold text-gray-800" style={font}>{t}</div><div className="text-[7.5px] text-gray-400" style={font}>{s}</div></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
        {/* Office photo strip */}
        <motion.div variants={fadeUp} className="mt-14 grid sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
          {[[PHOTO_OFFICE, "Teams run their whole day inside Dnexus CRM"], [PHOTO_TEAM, "From field staff to the boardroom — one platform"]].map(([src, cap]) => (
            <div key={cap} className="relative rounded-[22px] overflow-hidden shadow-[0_16px_44px_-14px_rgba(15,23,42,0.3)] group">
              <img src={src} alt={cap} loading="lazy" className="w-full h-48 object-cover group-hover:scale-[1.04] transition-transform duration-500" />
              <div className="absolute inset-x-0 bottom-0 px-5 py-3.5" style={{ background: "linear-gradient(0deg, rgba(15,23,42,.85), transparent)" }}>
                <div className="text-[13px] font-bold text-white" style={font}>{cap}</div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
}

// ==================== TESTIMONIALS ====================
const TESTIMONIALS = [
  { name: "Rakesh Sharma", company: "Growteq Agri Farms", photo: AVATARS[0], text: "We moved our lead follow-ups and field-team attendance to Dnexus CRM. Nothing slips through anymore — every lead gets a follow-up and I can see my team live on one screen." },
  { name: "Priya Verma", company: "SRMS Group", photo: AVATARS[1], text: "Managing multiple institutions was chaos before. With multi-business support, each unit has its own leads, staff, and reports — and I still get one combined view." },
  { name: "Amit Khandelwal", company: "Khandelwal Electronics", photo: AVATARS[2], text: "The GPS + selfie attendance ended all disputes about timing. Payroll that took two days now takes an hour, with salary slips generated automatically." },
  { name: "Neha Agarwal", company: "Bareilly Realty Co.", photo: AVATARS[3], text: "Very easy to use — my sales team adopted it in a day. WhatsApp integration and follow-up reminders alone doubled our client response rate." },
];

function Testimonials() {
  return (
    <Section className="py-20">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <motion.div variants={fadeUp} className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-[12px] font-bold tracking-[.14em] uppercase" style={{ ...font, color: GOLD_DEEP }}>Loved by businesses</span>
          <h2 className="mt-3 text-3xl lg:text-[40px] font-extrabold tracking-tight" style={{ ...fontHead, color: INK }}>What our customers say</h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TESTIMONIALS.map((t) => (
            <motion.div key={t.name} variants={fadeUp}
              className="bg-white rounded-[20px] border border-gray-100 p-6 shadow-[0_6px_24px_-8px_rgba(15,23,42,0.08)] hover:shadow-[0_16px_40px_-12px_rgba(15,23,42,0.15)] hover:-translate-y-1 hover:border-amber-200 transition-all flex flex-col">
              <div className="flex gap-0.5 mb-4">
                {Array.from({ length: 5 }).map((_, s) => <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
              </div>
              <p className="text-[13.5px] leading-relaxed text-gray-600 flex-1" style={font}>"{t.text}"</p>
              <div className="mt-5 flex items-center gap-3">
                <img src={t.photo} alt={t.name} loading="lazy" className="w-11 h-11 rounded-full object-cover border-2 shadow" style={{ borderColor: "#FDE68A" }} />
                <div>
                  <div className="text-[13.5px] font-bold" style={{ ...font, color: INK }}>{t.name}</div>
                  <div className="text-[11.5px] text-gray-400" style={font}>{t.company}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ==================== FAQ ====================
const FAQS = [
  { q: "How does the free trial work?", a: "Sign up and get full access to every Dnexus CRM feature for 10 days — no credit card required. Our team helps you set up for free. At the end of the trial, pick a plan to continue; your data stays exactly where you left it." },
  { q: "Can I manage multiple businesses?", a: "Yes. Dnexus CRM is multi-business by design. Each business gets its own leads, employees, attendance, and reports with individual settings, and you can switch between them from a single login." },
  { q: "Is there a mobile app?", a: "Yes. Your team can punch attendance with GPS, selfie, or QR, manage leads and tasks, apply for leave, and view payslips from their phone. Admins get live dashboards on the go." },
  { q: "Can I add more users later?", a: "Absolutely. You can add users any time as your team grows, and upgrade your plan whenever you need more seats or businesses — your data and settings carry over instantly." },
  { q: "Is my data secure?", a: "Your data lives on secure cloud infrastructure with encrypted connections, role-based permissions, per-business isolation, and regular backups. Only the people you authorize can see each module." },
];

function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <Section id="faq" className="py-20" style={{ background: "linear-gradient(180deg,#FFFBEB,#FFFFFF)" }}>
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <motion.div variants={fadeUp} className="text-center mb-12">
          <span className="text-[12px] font-bold tracking-[.14em] uppercase" style={{ ...font, color: GOLD_DEEP }}>FAQ</span>
          <h2 className="mt-3 text-3xl lg:text-[38px] font-extrabold tracking-tight" style={{ ...fontHead, color: INK }}>Frequently asked questions</h2>
        </motion.div>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <motion.div key={f.q} variants={fadeUp} className="bg-white rounded-2xl border border-gray-100 shadow-[0_4px_16px_-8px_rgba(15,23,42,0.08)] overflow-hidden">
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left">
                <span className="text-[15px] font-bold" style={{ ...font, color: INK }}>{f.q}</span>
                <ChevronDown className={`w-5 h-5 shrink-0 transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} style={{ color: GOLD_DEEP }} />
              </button>
              <div className={`grid transition-all duration-300 ${open === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                <div className="overflow-hidden">
                  <p className="px-6 pb-5 text-[14px] leading-relaxed text-gray-500" style={font}>{f.a}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ==================== FINAL CTA ====================
function FinalCta() {
  return (
    <Section className="py-16">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <motion.div variants={fadeUp} className="relative overflow-hidden rounded-[28px] px-8 py-14 text-center text-white">
          {/* Office photo background with ink overlay */}
          <img src={PHOTO_CTA} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(15,23,42,.93), rgba(15,23,42,.82))" }} />
          <div className="absolute -top-24 right-10 w-72 h-72 rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(239,168,0,.22)" }} />
          <div className="relative">
            <img src={LOGO_LIGHT} alt="Dnexus CRM" className="h-12 w-auto mx-auto mb-6" />
            <h2 className="text-3xl lg:text-[40px] font-extrabold tracking-tight" style={fontHead}>Ready to Grow Your Business?</h2>
            <p className="mt-4 text-[15.5px] text-gray-300 max-w-xl mx-auto" style={font}>Start managing your business smarter with Dnexus CRM.</p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3.5">
              <GoldBtn to="/login" testId="final-trial-btn" big>Start Free 10-Day Trial <ArrowRight className="w-4 h-4" /></GoldBtn>
              <button type="button" onClick={openScheduleDemo} data-testid="final-demo-btn"
                className="inline-flex items-center justify-center gap-2 text-[15px] font-bold text-white border-2 border-white/30 rounded-full px-8 py-3.5 hover:bg-white/10 transition-colors" style={font}>
                Schedule Demo
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

// ==================== FOOTER ====================
function Footer() {
  const cols = [
    { title: "Product", links: [["Features", "#features"], ["Pricing", "#pricing"], ["Integrations", "#features"], ["Mobile App", "#screenshots"]] },
    { title: "Company", links: [["About", "mailto:info@dnexusmedia.com?subject=About Dnexus CRM"], ["Contact", "mailto:info@dnexusmedia.com"], ["Careers", "mailto:info@dnexusmedia.com?subject=Careers"], ["Blog", "https://dnexusmedia.com"]] },
    { title: "Support", links: [["Help Center", "mailto:info@dnexusmedia.com?subject=Help"], ["Documentation", "mailto:info@dnexusmedia.com?subject=Documentation"], ["Privacy Policy", "/privacy-policy"], ["Terms", "/terms-of-service"]] },
  ];
  const social = [
    { label: "WhatsApp", href: "https://wa.me/917310692254", icon: MessageCircle },
    { label: "Website", href: "https://dnexusmedia.com", icon: Globe },
    { label: "Email", href: "mailto:info@dnexusmedia.com", icon: Mail },
  ];
  return (
    <footer className="border-t border-gray-100" style={{ background: "#FFFDF5" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10">
          <div className="col-span-2">
            <img src={LOGO_DARK} alt="Dnexus CRM" className="h-12 w-auto mb-5" />
            <p className="text-[13px] leading-relaxed text-gray-500 max-w-xs mb-5" style={font}>
              Dnexus CRM — the Business Operating System for leads, HR, attendance, tasks, and multi-business operations.
            </p>
            <div className="space-y-2 text-[13px] text-gray-500" style={font}>
              <div className="font-bold" style={{ color: INK }}>Digital Nexus Media Pvt. Ltd.</div>
              <a href="mailto:info@dnexusmedia.com" className="flex items-center gap-2 transition-colors hover:text-amber-700"><Mail className="w-3.5 h-3.5" /> info@dnexusmedia.com</a>
              <a href="tel:+917310692254" className="flex items-center gap-2 transition-colors hover:text-amber-700"><Phone className="w-3.5 h-3.5" /> +91 73106 92254</a>
              <a href="https://dnexusmedia.com" className="flex items-center gap-2 transition-colors hover:text-amber-700"><Globe className="w-3.5 h-3.5" /> dnexusmedia.com</a>
              <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> Bareilly, Uttar Pradesh, India</div>
            </div>
            <div className="flex gap-2.5 mt-5">
              {social.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" title={s.label}
                  className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:border-transparent transition-all"
                  onMouseEnter={(e) => { e.currentTarget.style.background = GOLD_GRAD; e.currentTarget.style.color = INK; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = ""; e.currentTarget.style.color = ""; }}>
                  <s.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
          {cols.map((c) => (
            <div key={c.title}>
              <div className="text-[12px] font-extrabold uppercase tracking-wider mb-4" style={{ ...font, color: INK }}>{c.title}</div>
              <div className="space-y-2.5">
                {c.links.map(([label, href]) =>
                  href.startsWith("/") ? (
                    <Link key={label} to={href} className="block text-[13.5px] text-gray-500 hover:text-amber-700 transition-colors" style={font}>{label}</Link>
                  ) : (
                    <a key={label} href={href} className="block text-[13.5px] text-gray-500 hover:text-amber-700 transition-colors" style={font}>{label}</a>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-12 pt-6 border-t border-amber-100 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-[12.5px] text-gray-400" style={font}>© {new Date().getFullYear()} Digital Nexus Media Pvt. Ltd. All rights reserved.</div>
          <Link to="/login" className="text-[12.5px] font-bold hover:underline" style={{ ...font, color: GOLD_DEEP }}>Start your free 10-day trial →</Link>
        </div>
      </div>
    </footer>
  );
}

// ==================== LIVE ALERTS (AnimatedList showcase) ====================
// Magic UI AnimatedList fed with real CRM event types — shows visitors the
// notification stream they'd get (leads, tasks, attendance, payments).
import { AnimatedList } from "@/components/ui/animated-list";
import { cn } from "@/lib/utils";

const LIVE_ALERTS = [
  { name: "New lead received",   description: "Rahul Sharma · Facebook Ads",       time: "just now", icon: "📢", color: "#6366F1" },
  { name: "Demo scheduled",      description: "Growteq Agri Farms · Website form",  time: "2m ago",   icon: "🗓️", color: "#EFA800" },
  { name: "Task completed",      description: "SEO Report — Rajshree Trust",        time: "5m ago",   icon: "✅", color: "#10B981" },
  { name: "Punched in",          description: "Jyoti Singh · 9:02 AM",              time: "10m ago",  icon: "⏰", color: "#0EA5E9" },
  { name: "Payment received",    description: "₹45,000 · Shri Radhe Travels",       time: "15m ago",  icon: "💸", color: "#00C9A7" },
  { name: "Lead stage updated",  description: "Saqlani Travels → Qualified",        time: "18m ago",  icon: "📈", color: "#8B5CF6" },
  { name: "New team member",     description: "Pradeep Kumar joined Growteq",       time: "25m ago",  icon: "👤", color: "#FFB800" },
  { name: "WhatsApp alert sent", description: "Demo enquiry shared with sales",     time: "30m ago",  icon: "💬", color: "#25D366" },
];

function AlertCard({ name, description, icon, color, time }) {
  return (
    <figure
      className={cn(
        "relative mx-auto min-h-fit w-full max-w-[400px] cursor-pointer overflow-hidden rounded-2xl p-4",
        "transition-all duration-200 ease-in-out hover:scale-[103%]",
        "bg-white [box-shadow:0_0_0_1px_rgba(0,0,0,.03),0_2px_4px_rgba(0,0,0,.05),0_12px_24px_rgba(0,0,0,.05)]"
      )}
    >
      <div className="flex flex-row items-center gap-3">
        <div className="flex size-10 items-center justify-center rounded-2xl" style={{ backgroundColor: color }}>
          <span className="text-lg">{icon}</span>
        </div>
        <div className="flex flex-col overflow-hidden">
          <figcaption className="flex flex-row items-center whitespace-pre text-lg font-medium" style={{ color: INK }}>
            <span className="text-sm sm:text-[15px] font-bold" style={font}>{name}</span>
            <span className="mx-1 text-gray-300">·</span>
            <span className="text-xs text-gray-400" style={font}>{time}</span>
          </figcaption>
          <p className="text-[13px] font-normal text-gray-500 truncate" style={font}>{description}</p>
        </div>
      </div>
    </figure>
  );
}

function LiveAlerts() {
  return (
    <Section className="py-20" id="live-alerts">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        {/* Copy */}
        <motion.div variants={fadeUp}>
          <span className="text-[12px] font-bold tracking-[.14em] uppercase" style={{ ...font, color: GOLD_DEEP }}>Real-time alerts</span>
          <h2 className="mt-3 text-3xl lg:text-[40px] font-extrabold tracking-tight" style={{ ...fontHead, color: INK }}>
            Never miss a lead, task, or punch-in again
          </h2>
          <p className="mt-5 text-[15.5px] leading-relaxed text-gray-500 max-w-lg" style={font}>
            Every event in your business — a new Facebook lead, a completed task, a team member
            punching in, a payment landing — appears instantly in your notification stream and on
            WhatsApp. Your team reacts in seconds, not hours.
          </p>
          <ul className="mt-6 space-y-2.5">
            {["Leads from Facebook, Google Sheets, WhatsApp & your website", "Task assignments, approvals and status changes", "Attendance punch-in/out alerts for admins", "Instant WhatsApp sharing to your sales team"].map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-[14px] text-gray-600" style={font}>
                <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: GOLD_DEEP }} /> {f}
              </li>
            ))}
          </ul>
        </motion.div>
        {/* Animated feed */}
        <motion.div variants={fadeUp}
          className="relative flex h-[480px] w-full flex-col overflow-hidden rounded-[24px] border border-amber-100 p-6"
          style={{ background: "linear-gradient(180deg,#FFFDF5,#FFF9E8)" }}
        >
          <AnimatedList delay={1400}>
            {LIVE_ALERTS.map((item, idx) => (
              <AlertCard {...item} key={idx} />
            ))}
          </AnimatedList>
          {/* Bottom fade so cards dissolve out */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24" style={{ background: "linear-gradient(to top, #FFF9E8, transparent)" }} />
        </motion.div>
      </div>
    </Section>
  );
}

// ==================== OUR CLIENTS ====================
// Same client-logo marquee as dnexusmedia.com's "Our Clients" section,
// restyled for this page's light gold/ink theme. Logos are served from the
// main site's /clients-original/ folder so both sites share one asset set.
const CLIENT_LOGOS = [
  "srms.webp", "rajshree-logo-3.jpg", "rohilkhand-1.jpg", "goodlife-logo-1.jpg", "prakash-logo.jpg",
  "sobti.jpg", "oviera.jpg", "eldeco.jpg", "amaya-city.jpg", "jewels-sky.jpg",
  "gurukul.jpg", "cambria.jpg", "aviraaj.jpg", "evynova.jpg", "gaptech.jpg",
  "skincity.jpg", "natraj-logo-1.jpg", "ashoka-foam-logo-1.jpg", "hayatbazar-1.jpg", "kipps-.jpg",
  "brain.jpg", "swami-ji-logo-1.jpg", "synaprics.jpg", "ultramod.jpg", "zeal.jpg",
].map((f) => `https://dnexusmedia.com/clients-original/${f}`);

function LogoRow({ logos, reverse }) {
  // Repeat 3× so the -50% translate loops seamlessly
  const cells = [...logos, ...logos, ...logos];
  return (
    <div className={`dnx-logo-row ${reverse ? "dnx-logo-row-reverse" : ""}`}>
      {cells.map((src, i) => (
        <div key={i} className="dnx-logo-cell">
          <img src={src} alt="Client logo" loading="lazy" />
        </div>
      ))}
    </div>
  );
}

function OurClients() {
  const half = Math.ceil(CLIENT_LOGOS.length / 2);
  return (
    <Section className="py-20" id="clients">
      <style>{`
        .dnx-logo-marquee {
          position: relative;
          overflow: hidden; /* rows are width:max-content for the scroll loop — must be clipped or they blow out page width on mobile */
          -webkit-mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent);
                  mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent);
        }
        .dnx-logo-row { display: flex; width: max-content; animation: dnx-marquee 60s linear infinite; }
        .dnx-logo-row-reverse { animation-direction: reverse; animation-duration: 70s; }
        .dnx-logo-marquee:hover .dnx-logo-row { animation-play-state: paused; }
        @keyframes dnx-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .dnx-logo-cell {
          flex: 0 0 auto; height: 88px; margin: 0 14px; padding: 14px 20px;
          display: inline-flex; align-items: center; justify-content: center;
          background: #fff; border-radius: 14px; border: 1px solid #F3F4F6;
          box-shadow: 0 6px 20px -8px rgba(15,23,42,.10);
          transition: transform .25s, box-shadow .25s, border-color .25s;
        }
        .dnx-logo-cell:hover {
          transform: translateY(-3px) scale(1.04);
          border-color: #FDE68A;
          box-shadow: 0 14px 34px -10px rgba(239,168,0,.35);
        }
        .dnx-logo-cell img { height: 56px; width: auto; max-width: 180px; object-fit: contain; display: block; }
        @media (max-width: 768px) {
          .dnx-logo-cell { height: 68px; margin: 0 8px; padding: 10px 14px; border-radius: 12px; }
          .dnx-logo-cell img { height: 44px; max-width: 140px; }
          .dnx-logo-row { animation-duration: 45s; }
          .dnx-logo-row-reverse { animation-duration: 50s; }
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <motion.div variants={fadeUp} className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-[12px] font-bold tracking-[.14em] uppercase" style={{ ...font, color: GOLD_DEEP }}>Our Clients</span>
          <h2 className="mt-3 text-3xl lg:text-[40px] font-extrabold tracking-tight" style={{ ...fontHead, color: INK }}>
            Trusted by <span style={{ background: "linear-gradient(90deg,#D99700,#A16207)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>200+ Brands</span> Across India
          </h2>
          <p className="mt-4 text-[15px] text-gray-500" style={font}>
            From leading hospitals and institutions to ambitious local brands — businesses across industries run on Dnexus.
          </p>
        </motion.div>
      </div>
      <motion.div variants={fadeUp} className="dnx-logo-marquee">
        <LogoRow logos={CLIENT_LOGOS.slice(0, half)} />
        <div className="mt-4">
          <LogoRow logos={CLIENT_LOGOS.slice(half)} reverse />
        </div>
      </motion.div>
    </Section>
  );
}

// ==================== SCHEDULE DEMO DIALOG ====================
// Opened from the Hero "Book a Live Demo" and Final-CTA "Schedule Demo"
// buttons via a window event, so no prop-threading across sections.
// Submits to the public /api/website-enquiry endpoint, which creates a lead
// (source: Website) + contact under Digital Nexus Media.
const DEMO_EVENT = "dnexus:schedule-demo";
export function openScheduleDemo() {
  try { window.dispatchEvent(new CustomEvent(DEMO_EVENT)); } catch (_) {}
}

function ScheduleDemoDialog() {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", preferred: "", message: "" });

  useEffect(() => {
    const onOpen = () => { setOpen(true); setSent(false); setError(""); };
    window.addEventListener(DEMO_EVENT, onOpen);
    return () => window.removeEventListener(DEMO_EVENT, onOpen);
  }, []);

  useEffect(() => {
    // Lock page scroll while the dialog is open
    if (open) { document.body.style.overflow = "hidden"; }
    else { document.body.style.overflow = ""; }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { setError("Name and email are required."); return; }
    setSending(true);
    setError("");
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const r = await fetch(`${backend}/api/website-enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          company: form.company,
          service: "CRM Live Demo Request",
          message: [
            form.preferred ? `Preferred demo slot: ${form.preferred.replace("T", " ")}` : "",
            form.message,
          ].filter(Boolean).join("\n\n"),
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "Something went wrong. Please try again.");
      }
      setSent(true);
      setForm({ name: "", email: "", phone: "", company: "", preferred: "", message: "" });
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally { setSending(false); }
  };

  // Explicit text colors: gray-800 for typed text, gray-400 for placeholders.
  // datetime-local has no real placeholder — WebKit renders the format hint in
  // the input's own color, so we swap gray-400 ↔ ink based on whether a value
  // is set (otherwise the hint was near-invisible on the gray background).
  const inputCls = "w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-[14px] text-gray-800 placeholder:text-gray-400 outline-none transition-colors focus:border-amber-400 focus:bg-white";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,.55)", backdropFilter: "blur(4px)" }}
      onClick={() => setOpen(false)} data-testid="schedule-demo-dialog">
      <div className="w-full max-w-lg bg-white rounded-[24px] shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="relative px-7 pt-7 pb-5" style={{ background: "linear-gradient(135deg,#FFF7E0,#FFFDF5)" }}>
          <button onClick={() => setOpen(false)} aria-label="Close" data-testid="schedule-demo-close"
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-800 transition-colors">
            <X className="w-4 h-4" />
          </button>
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-3" style={{ background: GOLD_GRAD }}>
            <CalendarDays className="w-5 h-5" style={{ color: INK }} />
          </div>
          <h3 className="text-[22px] font-extrabold tracking-tight" style={{ ...fontHead, color: INK }}>Schedule a Live Demo</h3>
          <p className="mt-1 text-[13.5px] text-gray-500" style={font}>See Dnexus CRM in action — 30 minutes, one-on-one, no commitment.</p>
        </div>

        {sent ? (
          <div className="px-7 py-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <Check className="w-7 h-7 text-emerald-600" />
            </div>
            <div className="text-[18px] font-extrabold" style={{ ...fontHead, color: INK }}>Request received!</div>
            <p className="mt-2 text-[14px] text-gray-500 max-w-sm mx-auto" style={font}>
              Our team will reach out shortly to confirm your demo slot. You can also WhatsApp us at +91 73106 92254.
            </p>
            <button onClick={() => setOpen(false)}
              className="mt-6 inline-flex items-center justify-center gap-2 text-[14.5px] font-bold rounded-full px-7 py-3 transition-transform hover:scale-[1.02]"
              style={{ ...font, background: GOLD_GRAD, color: INK }}>
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-7 py-6 space-y-3.5" style={font}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <input className={inputCls} placeholder="Your name *" value={form.name} onChange={set("name")} data-testid="demo-name" required />
              <input className={inputCls} placeholder="Work email *" type="email" value={form.email} onChange={set("email")} data-testid="demo-email" required />
              <input className={inputCls} placeholder="Phone / WhatsApp" type="tel" value={form.phone} onChange={set("phone")} data-testid="demo-phone" />
              <input className={inputCls} placeholder="Company name" value={form.company} onChange={set("company")} data-testid="demo-company" />
            </div>
            <div>
              <label className="block text-[12px] font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Preferred date &amp; time</label>
              <input className={inputCls} type="datetime-local" value={form.preferred} onChange={set("preferred")} data-testid="demo-preferred"
                style={{ color: form.preferred ? "#1F2937" : "#9CA3AF", colorScheme: "light" }} />
            </div>
            <textarea className={inputCls} rows={3} placeholder="Anything specific you'd like to see? (optional)" value={form.message} onChange={set("message")} data-testid="demo-message" />
            {error && <div className="text-[13px] font-semibold text-rose-600">{error}</div>}
            <button type="submit" disabled={sending} data-testid="demo-submit"
              className="w-full inline-flex items-center justify-center gap-2 text-[15px] font-bold rounded-full px-7 py-3.5 transition-transform hover:scale-[1.01] disabled:opacity-60"
              style={{ ...font, background: GOLD_GRAD, color: INK }}>
              {sending ? "Sending…" : <>Request Demo <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-center text-[11.5px] text-gray-400">We'll only use your details to arrange the demo. No spam.</p>
          </form>
        )}
      </div>
    </div>
  );
}

// ==================== PAGE ====================
export default function LandingPage() {
  return (
    <div className="bg-white min-h-screen overflow-x-hidden" style={font}>
      <Navbar />
      <Hero />
      <TrustStats />
      <OurClients />
      <Features />
      <LiveAlerts />
      <WhyChoose />
      <Pricing />
      <TrialBanner />
      <Screenshots />
      <Testimonials />
      <Faq />
      <FinalCta />
      <Footer />
      <ScheduleDemoDialog />
      {/* WhatsApp floating button */}
      <a href="https://wa.me/917310692254" target="_blank" rel="noopener noreferrer" data-testid="whatsapp-fab"
        className="fixed bottom-5 right-5 z-40 w-[52px] h-[52px] rounded-full bg-[#25D366] shadow-[0_10px_30px_-6px_rgba(37,211,102,0.6)] flex items-center justify-center hover:scale-110 transition-transform"
        title="Chat on WhatsApp">
        <MessageCircle className="w-6 h-6 text-white" />
      </a>
    </div>
  );
}
